import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Text } from 'grommet';
import PropTypes from 'prop-types';
// Relative rather than '@enrich/shared-lib': this file lives inside the package
// and the barrel re-exports it, so importing the barrel here is a cycle.
import { API } from '../../api';
import { openInNewTab } from '../../helpers/NewTabOpen';
import { ResumeContentViewer } from '../../components/ResumeContentViewer';
import { isUsableResume } from '../../helpers/resumeDocument';
import { CircleAlert, Document, DocumentDownload } from 'grommet-icons';

export const ResumeActions = ({
    contact,
    resumeContent,
    resumeLink,
    revalidate,
    showAdminActions = true,
}) => {
    const resumeContentRef = useRef(null);
    const isMounted = useRef(true);
    const [downloadBusy, setDownloadBusy] = useState(false);
    const [downloadError, setDownloadError] = useState('');
    const [statusError, setStatusError] = useState('');

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    const handleDownload = async () => {
        setDownloadBusy(true);
        setDownloadError('');
        try {
            const printTarget =
                resumeContentRef.current?.querySelector('.ProseMirror') ||
                resumeContentRef.current;
            if (!printTarget) throw new Error('Nothing to print');
            // Loaded on demand: html2canvas + jspdf are ~1MB, and this page
            // renders for every contact whether or not anyone downloads.
            const { downloadResumePdf } =
                await import('../../helpers/downloadResumePdf');
            await downloadResumePdf(
                printTarget,
                `${contact.attributes.firstName || 'resume'}.pdf`,
            );
        } catch (error) {
            console.log(error);
            if (isMounted.current) {
                setDownloadError('Could not build the PDF. Please try again.');
            }
        }
        if (isMounted.current) setDownloadBusy(false);
    };

    const displayResumeActions =
        contact.attributes.isApproved && !contact.attributes.isResumeReady;
    const isReadyForReview = [
        'shared',
        'edits_requested',
        'edits_completed',
        'approved',
    ].includes(contact.attributes.resumeStatus);
    const displayOnboardingStage =
        !contact.attributes.isOnboardingCompleted &&
        [
            'ceo_video',
            'scholar_agreement',
            'resume_process',
            'schedule_call',
            'elevator_pitch',
        ].includes(contact.attributes.onboardingStage);

    if (!displayResumeActions) return null;

    const hasUsableResume = isUsableResume(resumeContent, resumeLink);

    const handleStatusUpdate = async (status) => {
        setStatusError('');
        try {
            // requestResumeEdits resolves undefined on failure rather than
            // throwing, so an empty result is treated as an error too.
            const result = await updateResumeStatus(contact.id, status);
            if (!result) throw new Error('Resume status update failed');
        } catch (error) {
            console.log(error);
            if (isMounted.current) {
                setStatusError(
                    error.response?.data?.error?.message ||
                        'Could not update the resume. Please try again.',
                );
            }
            return;
        }
        revalidate();
    };

    return (
        <Box
            align="center"
            background="white"
            border={{
                color: 'enrich-green-light',
                size: '3px',
            }}
            gap="small"
            justify="center"
            pad="medium"
            round="6px">
            {resumeContent && (
                <Box width="100%">
                    <Box ref={resumeContentRef}>
                        <ResumeContentViewer content={resumeContent} />
                    </Box>
                    <Button
                        alignSelf="end"
                        size="small"
                        label="Download PDF"
                        icon={<DocumentDownload />}
                        busy={downloadBusy}
                        onClick={handleDownload}
                    />
                    {downloadError && (
                        <Text
                            size="small"
                            color="status-critical"
                            alignSelf="end"
                            role="alert">
                            {downloadError}
                        </Text>
                    )}
                </Box>
            )}
            <Box direction="row" gap="small">
                {!resumeContent && resumeLink && (
                    <Button
                        secondary
                        size="large"
                        label="View Resume"
                        icon={<Document />}
                        tip={resumeLink}
                        onClick={() => openInNewTab(resumeLink)}
                    />
                )}

                {showAdminActions && !contact.attributes.isResumeReady && (
                    <Box direction="row" gap="small">
                        {['shared', 'edits_completed'].includes(
                            contact.attributes.resumeStatus,
                        ) && (
                            <Box
                                background="enrich-green"
                                round="10px"
                                color="white">
                                <Button
                                    size="large"
                                    label="Submit edits"
                                    onClick={() =>
                                        handleStatusUpdate('edits_requested')
                                    }
                                    tip={`This student will receive a message requesting${
                                        contact.attributes.resumeStatus ==
                                        'edits_completed'
                                            ? ' additional'
                                            : ''
                                    } edits to their resume.`}
                                />
                            </Box>
                        )}

                        {hasUsableResume && (
                            <Button
                                primary
                                size="large"
                                label="Approve Resume"
                                onClick={() => handleStatusUpdate('approved')}
                                tip="This student will be able to apply to job matches."
                            />
                        )}
                        {statusError && (
                            <Text color="status-critical" role="alert">
                                {statusError}
                            </Text>
                        )}
                    </Box>
                )}
            </Box>
            <Box>
                {displayOnboardingStage && (
                    <Text>
                        {contact.attributes.firstName} is at the{' '}
                        {contact.attributes.onboardingStage?.replace('_', ' ')}{' '}
                        stage of onboarding.
                    </Text>
                )}
            </Box>
            <Box>
                {contact.attributes.resumeStatus == 'edits_requested' && (
                    <Text>
                        {contact.attributes.firstName} has received edit
                        suggestions for their resume.
                    </Text>
                )}
                {contact.attributes.resumeStatus == 'edits_completed' && (
                    <Box direction="row" gap="small">
                        <CircleAlert color="black" />
                        <Text>
                            {contact.attributes.firstName} has edited their
                            resume - please review.
                        </Text>
                    </Box>
                )}
                {!isReadyForReview && (
                    <Text textAlign="center">
                        {contact.attributes.firstName} has not yet submitted
                        their resume for review.
                    </Text>
                )}
            </Box>
        </Box>
    );
};

const updateResumeStatus = async (contactId, status) => {
    if (status == 'approved') {
        return await approveResume(contactId);
    }

    if (status == 'edits_requested') {
        return await requestResumeEdits(contactId);
    }

    console.log(
        'Error: could not find matching action for resume status ',
        status,
    );
    return null;
};

export const approveResume = async (contactId) => {
    try {
        const response = await API.post('/api/approve-resume', {
            data: {
                contact: contactId,
            },
        });
        return response.data.data;
    } catch (error) {
        console.log(error);
        // Re-thrown so callers can show why the approval was refused, such
        // as a contact with no usable resume.
        throw error;
    }
};

export const requestResumeEdits = async (contactId) => {
    try {
        const response = await API.post('/api/request-resume-edits', {
            data: {
                contact: contactId,
            },
        });
        return response.data.data;
    } catch (error) {
        console.log(error);
    }
};

export const submitResumeChanges = async (contactId, content) => {
    try {
        const response = await API.post('/api/submit-resume-changes', {
            data: {
                contact: contactId,
                content,
            },
        });
        return response.data.data;
    } catch (error) {
        console.log(error);
        throw error;
    }
};

export const rejectResume = async (contactId, reason) => {
    try {
        const response = await API.post('/api/reject-resume', {
            data: {
                contact: contactId,
                reason,
            },
        });
        return response.data.data;
    } catch (error) {
        console.log(error);
        throw error;
    }
};

export const fetchResumeComments = async (documentId) => {
    try {
        const response = await API.get(
            `/api/documents/${documentId}/resume-comments`,
        );
        return response.data.data;
    } catch (error) {
        console.log(error);
        return [];
    }
};

export const createResumeComment = async (documentId, blockId, text) => {
    try {
        const response = await API.post('/api/resume-comments', {
            data: {
                document: documentId,
                blockId,
                text,
            },
        });
        return response.data.data;
    } catch (error) {
        console.log(error);
        throw error;
    }
};

export const resolveResumeComment = async (commentId) => {
    try {
        const response = await API.put(
            `/api/resume-comments/${commentId}/resolve`,
        );
        return response.data.data;
    } catch (error) {
        console.log(error);
        throw error;
    }
};

ResumeActions.propTypes = {
    contact: PropTypes.shape({
        id: PropTypes.string,
        attributes: PropTypes.shape({
            firstName: PropTypes.string,
            isApproved: PropTypes.bool,
            isResumeReady: PropTypes.bool,
            isOnboardingCompleted: PropTypes.bool,
            onboardingStage: PropTypes.string,
            resumeStatus: PropTypes.string,
        }),
    }),
    resumeContent: PropTypes.string,
    resumeLink: PropTypes.string,
    revalidate: PropTypes.func,
    showAdminActions: PropTypes.bool,
};
